import { useAuth } from "context/AuthContext";
import React from "react";
import { useCommonStore } from "stores/common-store";
import {
  NOTIFICATION_LIMIT,
  SPOperatorID,
  SPSaleID,
  TypeNotification,
  UserType,
} from "utils/constants";
import { JA } from "utils/strings.ja";
import NotificationModal from "./modalNotification";
import useBoolean from "hooks/useBoolean";
import ModalConfirm from "parts/CameraManagement/Modal/ModalConfirm";
import { formatDatetimeCreateAt } from "utils";
import { NotificationService } from "context/notification/NotificationService";
import { NotificationUserService } from "context/notification/NotificationUserService";

const isUnreadBy = (item, username) => !(item.readBy || []).includes(username);

// readBy with the user added (read) or removed (unread)
const withReadBy = (item, username, read) => {
  const readBy = (item.readBy || []).filter((id) => id !== username);
  return { ...item, readBy: read ? [...readBy, username] : readBy };
};

// Unread count for a badge. Only the newest page is loaded (NOTIFICATION_LIMIT
// items), so a full page of unread items with more behind it reads "30+".
const badgeText = (count, hasMore) =>
  count >= NOTIFICATION_LIMIT && hasMore ? `${NOTIFICATION_LIMIT}+` : String(count);

const CloseIcon = () => (
  <svg className="w-3.5 h-3.5" aria-hidden="true" fill="none" viewBox="0 0 14 14">
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
  </svg>
);

// tone: "primary" (blue) or "muted" (grey)
const BADGE_TONES = {
  primary: "bg-brand text-white",
  muted: "bg-slate-200 text-slate-500",
};
const Badge = ({ children, tone = "primary" }) => (
  <span
    className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold leading-5 ${BADGE_TONES[tone]}`}
  >
    {children}
  </span>
);

export function NotificationPart() {
  const {
    showNotificationPanel,
    toggleShowNotificationPanel,
    toggleShowNotificationUnread,
    notificationData,
    setNotificationData,
  } = useCommonStore((state) => state);
  const { loginData } = useAuth();
  const isNTTAdmin = loginData.type === UserType.NTTsp;

  const [isShowModalFrom, openModalFrom, closeModalFrom] = useBoolean();
  const [isShowModalConfirm, openModalConfirm, closeModalConfirm] =
    useBoolean();

  const [listItem, setListItem] = React.useState([]);
  const [activeTab, setActiveTab] = React.useState(TypeNotification.NTT);

  const [nextToken, setNextToken] = React.useState("");
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isCrudLoading, setIsCrudLoading] = React.useState(false);

  const [activateItem, setActivateItem] = React.useState({
    id: null,
    title: "",
    redirectURL: "",
    target: "",
  });

  const panelRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  const serviceNotification = new NotificationService(loginData);
  const serviceNotificationUser = new NotificationUserService(loginData);

  // The only target a non-admin sees
  const ownTarget = [SPOperatorID, SPSaleID].includes(loginData.spID)
    ? TypeNotification.NTT
    : TypeNotification.OEM;
  const currentTarget = isNTTAdmin ? activeTab : ownTarget;

  // Unread counts. The open tab counts what the panel has loaded; any other tab
  // counts the newest page the header loaded at startup (notificationData),
  // which this panel keeps in step with every change it makes.
  const storeItems = notificationData?.items ?? [];
  const unreadFor = (target) => {
    if (isLoaded && target === currentTarget) {
      return {
        count: listItem.filter((item) => item.isUnRead).length,
        hasMore: !!nextToken,
      };
    }
    const items = storeItems.filter((item) => item.target === target);
    return {
      count: items.filter((item) => isUnreadBy(item, loginData.username)).length,
      hasMore: items.length >= NOTIFICATION_LIMIT,
    };
  };
  const targets = isNTTAdmin
    ? [TypeNotification.NTT, TypeNotification.OEM]
    : [ownTarget];
  const unread = Object.fromEntries(targets.map((t) => [t, unreadFor(t)]));
  const totalUnread = targets.reduce((sum, t) => sum + unread[t].count, 0);
  const totalHasMore = targets.some((t) => unread[t].hasMore);

  // the bell's dot
  React.useEffect(() => {
    toggleShowNotificationUnread(totalUnread > 0);
  }, [totalUnread]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open on the tab with something unread (NTT first) — decided once, when the
  // header's copy first arrives. The panel rewrites that copy on every read /
  // unread / create / delete, and re-deciding then would yank the user off the
  // tab they are working in.
  const initialTabChosen = React.useRef(false);
  React.useEffect(() => {
    if (!isNTTAdmin || initialTabChosen.current || !notificationData?.items) return;
    initialTabChosen.current = true;
    const hasUnread = (target) =>
      storeItems.some(
        (item) => item.target === target && isUnreadBy(item, loginData.username)
      );
    if (hasUnread(TypeNotification.NTT)) setActiveTab(TypeNotification.NTT);
    else if (hasUnread(TypeNotification.OEM)) setActiveTab(TypeNotification.OEM);
  }, [notificationData]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!showNotificationPanel) {
      setNextToken("");
      setIsLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoaded(false);
      await fetchNoti();
      if (!cancelled) setIsLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [showNotificationPanel, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on a click outside the panel or on Escape — but not while one of its
  // own dialogs is open (they render outside the panel), and not on the bell,
  // which toggles it itself.
  React.useEffect(() => {
    if (!showNotificationPanel || isShowModalFrom || isShowModalConfirm) {
      return undefined;
    }
    const close = () => toggleShowNotificationPanel(false);
    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      if (event.target.closest?.("[data-notification-toggle]")) return;
      close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showNotificationPanel, isShowModalFrom, isShowModalConfirm]); // eslint-disable-line react-hooks/exhaustive-deps

  // load the next page when the end of the list scrolls into view
  React.useEffect(() => {
    if (!sentinelRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          listItem.length > 0 &&
          nextToken &&
          !debounceRef.current
        ) {
          debounceRef.current = setTimeout(async () => {
            setIsLoadingMore(true);
            await fetchNoti(true);
            debounceRef.current = null;
            setIsLoadingMore(false);
          }, 500);
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [listItem, nextToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNoti = async (isLoadMore = false) => {
    try {
      const token = isLoadMore ? nextToken : "";
      const res =
        currentTarget === TypeNotification.NTT
          ? await serviceNotification.getListNotificationNTT(token)
          : await serviceNotification.getListNotificationOEM(token);

      setNextToken(res.nextToken || "");
      const listResult = res.items.map((item) => ({
        ...item,
        isUnRead: isUnreadBy(item, loginData.username),
      }));
      setListItem((prev) => (isLoadMore ? [...prev, ...listResult] : listResult));
    } catch (error) {
      console.log("[getListNotification][error]: ", error);
    }
  };

  // Mirror a change into the header's copy, so the other tab's badge and the
  // bell stay right. `update` maps an item (return null to drop it). Reads the
  // store's latest copy, since mark-all-read calls this several times before
  // the component re-renders.
  const syncStore = (update) => {
    const latest = useCommonStore.getState().notificationData;
    if (!latest?.items) return;
    setNotificationData({
      ...latest,
      items: latest.items.map(update).filter(Boolean),
    });
  };

  const handleCreate = async (newItem) => {
    setIsCrudLoading(true);
    const result = await serviceNotification.createNewNotification(newItem);
    setIsCrudLoading(false);
    if (!result) return;

    if (activeTab === result.target) {
      setListItem((prev) => [{ ...result, isUnRead: true }, ...prev]);
    }
    const latest = useCommonStore.getState().notificationData;
    if (latest?.items) {
      setNotificationData({ ...latest, items: [result, ...latest.items] });
    }
    handleCloseModalNoti();
  };

  const handleUpdate = async (updateItem) => {
    setIsCrudLoading(true);
    const result = await serviceNotification.updateNotification(updateItem);
    setIsCrudLoading(false);
    if (!result) return;

    setListItem((prev) =>
      prev.map((item) =>
        item.id === updateItem.id ? { ...item, ...result, isUnRead: true } : item
      )
    );
    syncStore((item) => (item.id === updateItem.id ? { ...item, ...result } : item));
    handleCloseModalNoti();
  };

  const handleDelete = async (deleteItem) => {
    setIsCrudLoading(true);
    const result = await serviceNotification.deleteNotification(deleteItem);
    setIsCrudLoading(false);
    if (!result) return;

    setListItem((prev) => prev.filter((item) => item.id != deleteItem.id));
    syncStore((item) => (item.id === deleteItem.id ? null : item));
    handleCloseModalNoti();
  };

  const handleCloseModalNoti = () => {
    closeModalFrom();
    closeModalConfirm();
    setActivateItem({ id: null, title: "", redirectURL: "", target: "" });
  };

  // Set one notification read or unread: local state first, then the API,
  // undone if the API call fails.
  const setRead = async (noti, read) => {
    const apply = (isRead) => {
      const patch = (item) =>
        item.id === noti.id
          ? { ...withReadBy(item, loginData.username, isRead), isUnRead: !isRead }
          : item;
      setListItem((prev) => prev.map(patch));
      syncStore((item) =>
        item.id === noti.id ? withReadBy(item, loginData.username, isRead) : item
      );
    };

    apply(read);
    try {
      await serviceNotificationUser.updateNotificationStatus(
        noti,
        loginData.username,
        read ? "read" : "unread"
      );
    } catch (error) {
      console.error(`Error marking notification as ${read ? "read" : "unread"}:`, error);
      apply(!read);
    }
  };

  // open the link and mark it read
  const openNotification = (noti) => {
    if (noti.redirectURL) window.open(noti.redirectURL, "_blank", "noopener");
    if (noti.isUnRead) setRead(noti, true);
  };

  return (
    <>
      <aside
        ref={panelRef}
        aria-label={JA.notification.title}
        // starts at the header's bottom edge (--app-header-h, set by the header)
        style={{
          top: "var(--app-header-h, 4rem)",
          height: "calc(100vh - var(--app-header-h, 4rem))",
        }}
        className={`fixed right-0 z-999 flex w-96 flex-col border-l border-stroke bg-white shadow-xl transition-transform duration-300 dark:border-strokedark dark:bg-boxdark ${
          showNotificationPanel ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-stroke dark:border-strokedark">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            {JA.notification.title}
          </h2>
          {totalUnread > 0 && <Badge>{badgeText(totalUnread, totalHasMore)}</Badge>}
          <div className="ml-auto flex items-center gap-1">
            {isNTTAdmin && (
              <button
                type="button"
                title={JA.notification.create}
                aria-label={JA.notification.create}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-brand hover:bg-brand-soft"
                onClick={() => {
                  setActivateItem({ target: activeTab });
                  openModalFrom();
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            )}
            <button
              type="button"
              aria-label={JA.common.close}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-black"
              onClick={() => toggleShowNotificationPanel(false)}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* NTT / OEM tabs (NTT admin only): a filter, so underlined rather than filled */}
        {isNTTAdmin && (
          <div
            role="tablist"
            className="flex px-4 border-b border-stroke dark:border-strokedark"
          >
            {[TypeNotification.NTT, TypeNotification.OEM].map((target) => (
              <button
                key={target}
                type="button"
                role="tab"
                aria-selected={activeTab === target}
                className={`-mb-px inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-sm font-semibold transition-colors ${FOCUS_RING} ${
                  activeTab === target
                    ? "border-brand text-brand"
                    : "border-transparent text-slate-500 hover:text-brand dark:text-slate-400"
                }`}
                onClick={() => setActiveTab(target)}
              >
                {target}
                {unread[target].count > 0 && (
                  <Badge>{badgeText(unread[target].count, unread[target].hasMore)}</Badge>
                )}
              </button>
            ))}
          </div>
        )}

        {/* list */}
        <div className="flex-1 overflow-auto">
          {isCrudLoading && (
            <div className="flex items-center justify-center gap-2 py-3 border-b border-stroke text-sm text-slate-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
              {JA.common.processing}
            </div>
          )}

          {groupByDay(listItem).map(({ key, label, items }) => (
            <section key={key} aria-label={label}>
              <h3 className="sticky top-0 z-1 border-b border-stroke bg-white px-4 py-1.5 text-[13px] font-semibold text-slate-500 dark:border-strokedark dark:bg-boxdark dark:text-slate-400">
                {label}
              </h3>
              <ul>
                {items.map((item) => (
                  <ItemMenuNotification
                    key={item.id}
                    data={item}
                    editMode={isNTTAdmin}
                    onOpen={openNotification}
                    onToggleRead={(noti) => setRead(noti, noti.isUnRead)}
                    handleUpdate={(detail) => {
                      setActivateItem(detail);
                      openModalFrom();
                    }}
                    handleDelete={(detail) => {
                      setActivateItem(detail);
                      openModalConfirm();
                    }}
                  />
                ))}
              </ul>
            </section>
          ))}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {!isLoaded && <ItemNotificationSkeleton />}
          {isLoadingMore && <PaginationLoadingIndicator />}
          {isLoaded && listItem.length === 0 && <EmptyState />}
        </div>
      </aside>

      {isShowModalFrom && showNotificationPanel && (
        <NotificationModal
          isOpen={isShowModalFrom}
          handleCloseModal={handleCloseModalNoti}
          handleUpdateNotication={(item) => {
            if (!activateItem.id) handleCreate(item);
            else handleUpdate(item);
          }}
          detailNotification={{ ...activateItem }}
        />
      )}
      {isShowModalConfirm && showNotificationPanel && (
        <ModalConfirm
          title={JA.notification.delete}
          primaryButton={JA.common.delete}
          isOpen={isShowModalConfirm}
          closeModal={handleCloseModalNoti}
          handleOk={() => handleDelete(activateItem)}
        />
      )}
    </>
  );
}

const pad = (n) => n.toString().padStart(2, "0");
const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

// 今日 / 昨日 / 10月18日, with the year only when it isn't this year
const dayLabel = (date, now = new Date()) => {
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayKey(date) === dayKey(now)) return JA.notification.today;
  if (dayKey(date) === dayKey(yesterday)) return JA.notification.yesterday;
  const monthDay = `${date.getMonth() + 1}月${date.getDate()}日`;
  return date.getFullYear() === now.getFullYear()
    ? monthDay
    : `${date.getFullYear()}年${monthDay}`;
};

// Newest first, one group per local day. Sorted here because an edit changes
// an item's updatedAt without moving it in the list.
const groupByDay = (items) => {
  const groups = [];
  [...items]
    .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))
    .forEach((item) => {
      const date = new Date(item.updatedAt);
      const key = dayKey(date);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(item);
      else groups.push({ key, label: dayLabel(date), items: [item] });
    });
  return groups;
};

// where the link goes, shown so the user knows before clicking
const hostOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch (e) {
    return "";
  }
};

const FOCUS_RING = "outline-hidden focus-visible:ring-2 focus-visible:ring-brand";

// shown on hover / keyboard focus of the row, always on touch screens (tailwind.css)
const ROW_HOVER_ONLY =
  "notification-row-actions opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none";

const RowIconButton = ({ label, danger = false, onClick, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${FOCUS_RING} ${
      danger
        ? "text-slate-500 hover:bg-red-50 hover:text-red-600"
        : "text-slate-500 hover:bg-slate-100 hover:text-brand"
    }`}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  </button>
);

// One notification. Unread rows carry a blue rule and a filled dot; read rows
// show the dot's ring only on hover. The dot toggles read / unread, the rest of
// the row opens the link.
const ItemMenuNotification = React.memo(
  ({ data, editMode, onOpen, onToggleRead, handleUpdate, handleDelete }) => {
    const date = new Date(data.updatedAt);
    const host = hostOf(data.redirectURL);
    const toggleLabel = data.isUnRead ? JA.notification.markRead : JA.notification.markUnread;
    return (
      <li
        className={`group relative flex items-center gap-1 border-b border-stroke py-3 pl-2 pr-3 dark:border-strokedark ${
          data.isUnRead ? "bg-brand-soft dark:bg-white/5" : ""
        }`}
      >
        {data.isUnRead && (
          <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-brand" />
        )}

        <button
          type="button"
          title={toggleLabel}
          aria-label={toggleLabel}
          onClick={() => onToggleRead(data)}
          className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-slate-200 ${FOCUS_RING}`}
        >
          <span
            className={`h-3 w-3 rounded-full ${
              data.isUnRead
                ? "bg-brand"
                : `${ROW_HOVER_ONLY} border-[1.5px] border-slate-300 dark:border-slate-500`
            }`}
          />
        </button>

        <button
          type="button"
          onClick={() => onOpen(data)}
          className={`min-w-0 flex-1 rounded-sm text-left ${FOCUS_RING}`}
        >
          <span
            title={data.title}
            className={`block line-clamp-2 wrap-break-word text-[15px] leading-relaxed ${
              data.isUnRead
                ? "font-semibold text-black dark:text-white"
                : "text-slate-700 dark:text-slate-300"
            }`}
          >
            {data.title}
          </span>
          <span className="mt-1 flex items-center gap-3 text-[13px] text-slate-500 dark:text-slate-400">
            <time
              dateTime={data.updatedAt}
              title={formatDatetimeCreateAt(data.updatedAt)}
              className="tabular-nums"
            >
              {`${pad(date.getHours())}:${pad(date.getMinutes())}`}
            </time>
            {host && <span className="min-w-0 truncate">{host}</span>}
          </span>
        </button>

        {/* stacked so the title keeps its width */}
        {editMode && (
          <div className={`${ROW_HOVER_ONLY} -my-1 ml-2 flex shrink-0 flex-col items-center`}>
            <RowIconButton label={JA.common.edit} onClick={() => handleUpdate(data)}>
              <path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" />
            </RowIconButton>
            <RowIconButton label={JA.common.delete} danger onClick={() => handleDelete(data)}>
              <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12M9 7V4h6v3" />
            </RowIconButton>
          </div>
        )}
      </li>
    );
  }
);

const ItemNotificationSkeleton = React.memo(() => (
  <div aria-hidden="true" className="animate-pulse motion-reduce:animate-none">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex gap-3 border-b border-stroke py-3 pl-4 pr-3 dark:border-strokedark">
        <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-slate-100 dark:bg-meta-4" />
        <div className="flex-1">
          <div className="mb-2 h-3 w-4/5 rounded-sm bg-slate-100 dark:bg-meta-4" />
          <div className="h-2.5 w-1/3 rounded-sm bg-slate-100 dark:bg-meta-4" />
        </div>
      </div>
    ))}
  </div>
));

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-slate-400">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
    <p className="text-sm">{JA.notification.none}</p>
  </div>
);

const PaginationLoadingIndicator = React.memo(() => (
  <div className="flex items-center justify-center py-4">
    <div className="flex items-center space-x-2">
      <div className="w-1 h-1 bg-brand rounded-full animate-bounce"></div>
      <div className="w-1 h-1 bg-brand rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
      <div className="w-1 h-1 bg-brand rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
      <span className="ml-2 text-xs text-slate-500">{JA.common.loading}</span>
    </div>
  </div>
));
